import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, Sparkles, Loader2, Play } from "lucide-react";
import { exportTrainingPlanPDF } from "@/lib/pdf-export";
import { formatExercisePrescription, normalizeDiscipline, getDiscipline, resolveExerciseModality } from "@/lib/trainingDisciplines";
import DisciplineBadge from "@/components/training/DisciplineBadge";

function getEmbedUrl(video) {
  if (video.embed_url) return video.embed_url;
  if (video.video_file_url) return video.video_file_url;
  const url = video.video_url || "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

export default function MyTrainingPlan() {
  const [plans, setPlans] = useState([]);
  const [aiPdfLoading, setAiPdfLoading] = useState(null);
  const [videoModal, setVideoModal] = useState(null);
  const [videoLoading, setVideoLoading] = useState(null);
  const [liftMaxes, setLiftMaxes] = useState([]);
  const [sportRefs, setSportRefs] = useState([]);

  const openExerciseVideo = async (exerciseName) => {
    setVideoLoading(exerciseName);
    try {
      const { data } = await api.get("/exercise-videos/student-search", { params: { q: exerciseName } });
      if (data?.length > 0) {
        setVideoModal(data[0]);
      } else {
        toast.info("Aucune vidéo trouvée pour cet exercice");
      }
    } catch {
      toast.error("Vidéos non disponibles");
    } finally {
      setVideoLoading(null);
    }
  };

  const handleAiPdf = async (plan) => {
    setAiPdfLoading(plan.plan_id);
    try {
      const { data } = await api.post('/ai/pdf-analysis', { type: 'training' });
      exportTrainingPlanPDF(plan, data.analysis);
      toast.success("PDF avec analyse IA généré !");
    } catch {
      toast.error("Erreur lors de l'analyse IA");
    } finally {
      setAiPdfLoading(null);
    }
  };

  useEffect(() => {
    api.get('/training-plans').then(r => setPlans(r.data)).catch(() => {});
    api.get('/lift-maxes').then(r => setLiftMaxes(r.data || [])).catch(() => {});
    api.get('/sport-refs').then(r => setSportRefs(r.data || [])).catch(() => {});
  }, []);

  const prescriptionCtx = { liftMaxes, sportRefs };

  return (
    <div data-testid="my-training-plan-page">
      <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Mon Entraînement</h1>
      <p className="text-slate-500 mt-1">{plans.length} programme{plans.length > 1 ? 's' : ''}</p>

      {plans.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            {(() => {
              const EmptyIcon = getDiscipline("strength").icon;
              return <EmptyIcon className="w-8 h-8 text-slate-400" />;
            })()}
          </div>
          <p className="text-slate-500">Aucun programme d'entraînement assigné.</p>
          <p className="text-sm text-slate-400 mt-1">Votre coach vous enverra votre programme personnalisé.</p>
        </div>
      ) : (
        <div className="space-y-6 mt-6">
          {plans.map(plan => {
            const discipline = normalizeDiscipline(plan.discipline || 'strength');
            const planMeta = getDiscipline(discipline);
            const PlanIcon = planMeta.icon;
            return (
            <Card key={plan.plan_id} className={`border-slate-100 border-l-4 ${planMeta.accentBorder}`} data-testid={`training-plan-${plan.plan_id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${planMeta.light}`}>
                      <PlanIcon className={`w-5 h-5 ${planMeta.accent}`} />
                    </div>
                    <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="font-heading text-2xl font-bold text-slate-900">{plan.title}</h2>
                      <DisciplineBadge discipline={discipline} size="sm" />
                    </div>
                    {plan.description && <p className="text-slate-500 mt-2">{plan.description}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => exportTrainingPlanPDF(plan)}>
                      <Download className="w-4 h-4" /> PDF
                    </Button>
                    <Button
                      size="sm"
                      className="gap-1.5 bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={() => handleAiPdf(plan)}
                      disabled={aiPdfLoading === plan.plan_id}
                    >
                      {aiPdfLoading === plan.plan_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      PDF + IA
                    </Button>
                  </div>
                </div>

                {plan.days?.length > 0 && (
                  <div className="mt-6 space-y-4">
                    {plan.days.map((day, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl p-4">
                        <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">{day.day}</Badge>
                        </h4>
                        <div className="space-y-2">
                          {day.exercises?.map((ex, j) => {
                            const prescription = formatExercisePrescription(ex, discipline, prescriptionCtx);
                            const exMod = resolveExerciseModality(ex, discipline);
                            const showModBadge = discipline === "hybrid" && exMod !== discipline;
                            return (
                            <div key={j} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 gap-2">
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-medium text-slate-900">{ex.name}</span>
                                  {showModBadge && <DisciplineBadge discipline={exMod} size="sm" />}
                                </div>
                                {prescription && (
                                  <p className="text-xs text-blue-600 mt-0.5">{prescription}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 shrink-0"
                                  onClick={() => openExerciseVideo(ex.name)}
                                  disabled={videoLoading === ex.name}
                                >
                                  {videoLoading === ex.name ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 text-purple-600" />}
                                </Button>
                              </div>
                            </div>
                          );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
          })}
        </div>
      )}

      <Dialog open={!!videoModal} onOpenChange={() => setVideoModal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{videoModal?.title}</DialogTitle></DialogHeader>
          {videoModal && (
            <div className="aspect-video rounded-xl overflow-hidden bg-black mt-2">
              <iframe src={getEmbedUrl(videoModal)} title={videoModal.title} className="w-full h-full" allowFullScreen />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}